import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { missingContact } from "@/lib/contact";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Trash2, IndianRupee } from "lucide-react";
import { prettyType } from "@/lib/serviceGroups";
import { useSiteConfig } from "@/lib/siteConfig";
import TermsAgreement from "@/components/TermsAgreement";
import { TERMS_VERSION } from "@/lib/terms";
import { oneLine } from "@/lib/address";

// Back to the wizard on the transaction that owns this cart line. Same shape the dashboard's Edit
// button builds — an affidavit names its template in `?type=`, anything else is its own route.
const editLink = (it) =>
  `/customer/wizard/${it.service_type === "affidavit"
    ? `affidavit?type=${it.subtype}&txn=${it.transaction_id}`
    : `${it.service_type}?txn=${it.transaction_id}`}`;

export default function CartPage() {
  const { gst_enabled: gstEnabled } = useSiteConfig();
  const [cart, setCart] = useState(null);
  const [addresses, setAddresses] = useState([]);
  // Starts unselected rather than at 0: with up to 5 saved addresses, silently defaulting to the
  // first is how an order gets couriered somewhere the customer didn't intend. Auto-selected
  // below only when there is exactly one, where there is nothing to choose between.
  const [addrIdx, setAddrIdx] = useState(null);
  // Unticked on every mount on purpose: agreeing to the terms is an act at the moment of paying,
  // not a setting, so it is never remembered from a previous visit or a previous order.
  const [agreed, setAgreed] = useState(false);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  // Per-item checkbox state, keyed by transaction id — missing means checked, so every item
  // defaults to selected the moment it appears (a fresh load, or a new item added to the cart)
  // without this having to reset itself against the cart's contents on every reload.
  const [unchecked, setUnchecked] = useState({});
  const isSelected = (tid) => !unchecked[tid];
  const toggleSelected = (tid) => setUnchecked(u => ({ ...u, [tid]: !u[tid] }));
  const nav = useNavigate();
  const load = () => {
    api.get("/cart").then(r => setCart(r.data)).catch(() => {});
    api.get("/auth/me").then(r => {
      const addrs = r.data.addresses || [];
      setAddresses(addrs);
      setMe(r.data);
      setAddrIdx(addrs.length === 1 ? 0 : null);
    });
  };
  useEffect(load, []);

  const remove = async (tid) => { await api.delete(`/cart/items/${tid}`); toast.success("Removed"); load(); };
  const checkout = async () => {
    setBusy(true);
    try {
      const item_ids = cart.items.map(it => it.transaction_id).filter(isSelected);
      // `agreed` rather than a literal true: the record must say what the customer actually did,
      // and a hardcoded true would keep claiming consent if this gate were ever loosened.
      const { data: o } = await api.post("/cart/checkout", {
        address_index: addrIdx, item_ids, terms_accepted: agreed, terms_version: TERMS_VERSION });
      toast.success("Payment complete! Bill emailed to you.");
      nav(`/customer?order=${o.id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "string" && d.includes("address")) { toast.error("Add a delivery address in profile first"); nav("/customer/profile"); }
      else toast.error(d || "Checkout failed");
    } finally { setBusy(false); }
  };

  if (!cart) return <div>Loading...</div>;
  const empty = cart.items.length === 0;
  // Mirrors core.lifecycle.require_customer_info, which the server enforces on /cart/checkout.
  // The button is disabled rather than left to fail so the customer is told what to fix, where.
  // Name, email and mobile come from `@/lib/contact` — the same rule the layout and profile page
  // apply. Checked only once the profile has loaded, so the warning does not flash on every visit.
  const missing = me ? [
    ...missingContact(me),
    ...(addresses.length ? [] : ["delivery address"]),
  ] : [];
  const mustPickAddress = addresses.length > 1 && addrIdx === null;
  const selectedItems = cart.items.filter(it => isSelected(it.transaction_id));
  const noneSelected = selectedItems.length === 0;
  // Only what's ticked is being paid for right now, so the summary and the completeness gate
  // below both read against `selectedItems`, not the whole cart — an unfinished document left
  // unticked shouldn't block paying for the ones that are ready.
  const selectedTotals = selectedItems.reduce((acc, it) => ({
    subtotal: acc.subtotal + it.line.subtotal, gst: acc.gst + it.line.gst, total: acc.total + it.line.total,
  }), { subtotal: 0, gst: 0, total: 0 });
  // Mirrors _reject_if_incomplete, which checkout enforces per item. An item can be added while
  // complete and edited back to blank afterwards — editing stays legal until payment — so the
  // cart is a real place for this to be true, not just a duplicate of the add-to-cart gate.
  const unfinished = selectedItems.filter(it => (it.incomplete_fields || []).length > 0);
  const canPay = !!me && !busy && !noneSelected && missing.length === 0 && !mustPickAddress && unfinished.length === 0 && agreed;

  return (
    <div className="space-y-6" data-testid="cart-page">
      <div>
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C]">Checkout</div>
        <h1 className="font-serif-display text-3xl md:text-4xl text-white mt-1">Your Cart</h1>
      </div>
      {empty ? (
        <Card><CardContent className="p-10 text-center text-[#6B7280]" data-testid="cart-empty">Your cart is empty.</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-[1fr_360px] gap-6">
          <Card>
            <CardHeader><CardTitle className="font-serif-display text-lg">Items ({cart.items.length})</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {cart.items.map(it => {
                const gaps = it.incomplete_fields || [];
                return (
                <div key={it.transaction_id} data-testid={`cart-item-${it.transaction_id}`}
                  className={`flex items-start justify-between gap-3 p-4 rounded border ${gaps.length ? "bg-[#F27373]/5 border-[#F27373]/40" : "bg-white/5"}`}>
                  <Checkbox data-testid={`cart-item-select-${it.transaction_id}`}
                    checked={isSelected(it.transaction_id)}
                    onCheckedChange={() => toggleSelected(it.transaction_id)}
                    className="mt-1 shrink-0"/>
                  <div className="flex-1">
                    {/* Their own name for it when they gave one, otherwise the one the server
                        assigns ("Rental-08955309") — so a cart holding three affidavits is never
                        three identical lines. The type sits underneath either way. */}
                    <div className="font-medium text-white">{it.display_name || it.document_name || prettyType(it.service_type)}</div>
                    <div className="text-[11px] text-[#6B7280]">{it.template_title || prettyType(it.service_type)}</div>
                    <div className="text-xs text-[#6B7280] mt-1">Stamp ₹{it.line.stamp_paper_cost} · Service ₹{it.line.service_cost} · Delivery ₹{it.line.delivery_cost}{gstEnabled ? ` · GST ₹${it.line.gst.toFixed(0)}` : ""}</div>
                    {/* Says *that* something is missing, not *what* — the wizard shows the blank
                        answers in the place they can be filled in, and reciting six field names
                        here is a list the customer has to memorise before going to look. */}
                    {gaps.length > 0 && (
                      <div data-testid={`cart-item-incomplete-${it.transaction_id}`} className="text-xs text-[#F27373] mt-2">
                        One or more required details are missing —{" "}
                        <Link to={editLink(it)} data-testid={`cart-item-edit-${it.transaction_id}`} className="underline font-medium">finish this document →</Link>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-serif-display text-lg text-white">₹{it.line.total.toFixed(0)}</div>
                    <Button data-testid={`cart-remove-${it.transaction_id}`} variant="ghost" size="sm" onClick={()=>remove(it.transaction_id)} className="text-[#F27373] mt-1"><Trash2 className="w-3.5 h-3.5 mr-1"/>Remove</Button>
                  </div>
                </div>
                );
              })}
            </CardContent>
          </Card>
          <Card className="h-fit sticky top-4">
            <CardHeader><CardTitle className="font-serif-display text-lg">Order Summary</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {/* Reflects only the ticked items — not the whole cart's `cart.totals` — so a
                  document left unticked doesn't inflate what's about to be paid for. */}
              <div className="flex justify-between"><span>Subtotal</span><span>₹{selectedTotals.subtotal.toFixed(2)}</span></div>
              {/* Not shown at all when GST is off (core.templates_data.gst_enabled — the GSTIN is
                  the switch, see backend/CLAUDE.md "GST is one switch"), rather than a "GST ₹0.00"
                  line: a zero-tax line reads as "we charged nothing", not "there is no tax here",
                  and this business isn't registered to charge it at all when it's off. */}
              {gstEnabled && (
                <div className="flex justify-between"><span>GST</span><span>₹{selectedTotals.gst.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between border-t pt-2 mt-2 font-serif-display text-lg text-white"><span>Grand Total</span><span>₹{selectedTotals.total.toFixed(2)}</span></div>
              {noneSelected && (
                <div data-testid="cart-none-selected" className="text-xs text-[#6B7280]">Select at least one item to pay for.</div>
              )}
              {addresses.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-1.5">Deliver to</div>
                  <Select value={addrIdx === null ? "" : String(addrIdx)} onValueChange={(v) => setAddrIdx(Number(v))}>
                    <SelectTrigger data-testid="cart-addr-select" className="h-auto py-2 text-left">
                      <SelectValue placeholder="Choose a delivery address…"/>
                    </SelectTrigger>
                    <SelectContent>
                      {addresses.map((a, i) => (
                        <SelectItem key={i} value={String(i)} data-testid={`cart-addr-${i}`}>
                          <div className="text-left">
                            <div className="font-medium text-xs">{a.label || `Address ${i + 1}`}</div>
                            <div className="text-xs text-[#6B7280]">{oneLine(a)}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {missing.length > 0 && (
                <div data-testid="cart-info-missing" className="mt-3 p-3 rounded border border-[#F27373]/40 bg-[#F27373]/5 text-xs text-[#F27373] space-y-2">
                  <div>
                    We need your <strong>{missing.join(" and ")}</strong> before you can pay — your
                    document is couriered to you, so we can't take payment without it.
                  </div>
                  <Link to="/customer/profile" data-testid="cart-fix-info" className="underline font-medium block">Add my details →</Link>
                </div>
              )}
              {unfinished.length > 0 && (
                <div data-testid="cart-incomplete-items" className="mt-3 p-3 rounded border border-[#F27373]/40 bg-[#F27373]/5 text-xs text-[#F27373]">
                  {unfinished.length === 1 ? "One document isn't finished" : `${unfinished.length} documents aren't finished`} —
                  we can't take payment for a document with blank fields, because that's what gets
                  drafted, stamped and couriered to you. Finish {unfinished.length === 1 ? "it" : "them"} above to pay.
                </div>
              )}
              {mustPickAddress && (
                <div data-testid="cart-pick-address" className="mt-3 text-xs text-[#DBA94C]">
                  Choose which address to deliver to before paying.
                </div>
              )}
              <TermsAgreement checked={agreed} onChange={setAgreed} testId="cart-terms-agree" />
              <Button data-testid="cart-checkout" disabled={!canPay} onClick={checkout} className="w-full mt-4 bg-[#CBA96C] text-white hover:bg-[#b8965a] h-11 disabled:opacity-40">
                <IndianRupee className="w-4 h-4 mr-1"/>{busy ? "Processing..." : `Pay ₹${selectedTotals.total.toFixed(0)} (Mock)`}
              </Button>
              <div className="text-xs text-[#6B7280] text-center pt-2">Bill PDF auto-generated & emailed on payment.</div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
